import * as React from 'react';
import {
  Box,
  Button,
  FormControl,
  FormHelperText,
  FormLabel,
  Stack,
} from '@mui/material';

import {
  ErrorList,
  FileCard,
  FullScreenMediaModal,
  MediaCard,
} from '../components';
import { iriToId, serverImageUrl } from '../util';
import { Upload } from '@phosphor-icons/react';
import { FieldProp } from 'react-typed-form';
import { Api, useApiCallable } from '../api';
import config from '../config';

const BUSY = '_busy';

type AcceptedType =
  | 'VIDEO_TYPE'
  | 'IMAGE_TYPE'
  | 'TRANSCRIPT_TYPE'
  | 'AUDIO_TYPE';

const getAcceptedTypes = (acceptedTypes?: AcceptedType[]) => {
  let accept: string[] = [];
  if (!acceptedTypes) {
    return [
      ...config.ACCEPTED_MEDIA_TYPES.VIDEO_TYPE,
      ...config.ACCEPTED_MEDIA_TYPES.IMAGE_TYPE,
      ...config.ACCEPTED_MEDIA_TYPES.AUDIO_TYPE,
    ];
  }
  if (acceptedTypes.includes('IMAGE_TYPE')) {
    accept = [...accept, ...config.ACCEPTED_MEDIA_TYPES.IMAGE_TYPE];
  }
  if (acceptedTypes.includes('TRANSCRIPT_TYPE')) {
    accept = [...accept, ...config.ACCEPTED_MEDIA_TYPES.TRANSCRIPT_TYPE];
  }
  if (acceptedTypes.includes('VIDEO_TYPE')) {
    accept = [...accept, ...config.ACCEPTED_MEDIA_TYPES.VIDEO_TYPE];
  }
  if (acceptedTypes.includes('AUDIO_TYPE')) {
    accept = [...accept, ...config.ACCEPTED_MEDIA_TYPES.AUDIO_TYPE];
  }
  return accept;
};

const getButtonLabel = (acceptedTypes?: AcceptedType[]) => {
  if (!acceptedTypes || acceptedTypes.length > 1) {
    return 'Upload file';
  }
  if (acceptedTypes[0] === 'IMAGE_TYPE') {
    return 'Upload Image';
  }
  if (acceptedTypes[0] === 'VIDEO_TYPE') {
    return 'Upload Video';
  }
  if (acceptedTypes[0] === 'TRANSCRIPT_TYPE') {
    return 'Upload Transcript File';
  }
  if (acceptedTypes[0] === 'AUDIO_TYPE') {
    return 'Upload Audio';
  }
};

type Props = {
  field: FieldProp<string | typeof BUSY | null>;
  label?: string;
  existingAsset?: Api.MediaUpload.BaseView | null;
  acceptedTypes?: AcceptedType[];
  existingImageId?: string | null;
  required?: boolean;
  helperText?: string;
  aspectRatio?: string;
};

export default function FieldMediaUpload({
  field,
  label,
  existingAsset,
  existingImageId,
  acceptedTypes,
  required,
  helperText,
  aspectRatio,
}: Props) {
  const [mimeType, setMimeType] = React.useState<undefined | string>(
    existingAsset?.mimeType ?? undefined,
  );
  const [modalMedia, setModalMedia] = React.useState<null | string>(null);
  const [uploadingFile, setUploadingFile] = React.useState<File | null>(null);
  const callApi = useApiCallable();

  const handleFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    const file = files[0];
    field.handleValueChange(BUSY);
    setUploadingFile(file);

    const response = await callApi<Api.MediaUpload.BaseView, object>(
      '/media-uploads',
      {
        method: 'POST',
        uploadFile: file,
        formBody: { type: 'content' },
      },
    );

    if (response) {
      field.handleValueChange(response.data['@id']);
      setMimeType(response.data.mimeType);
    } else {
      field.handleValueChange(null);
    }
  };

  const imagePreview = React.useMemo(() => {
    if (
      field.value !== null &&
      uploadingFile &&
      (uploadingFile['type'] === 'image/png' ||
        uploadingFile['type'] === 'image/jpeg')
    ) {
      return URL.createObjectURL(uploadingFile);
    } else if (
      field.value &&
      existingAsset?.['id'] &&
      (existingAsset.mimeType === 'image/png' ||
        existingAsset.mimeType === 'image/jpeg')
    ) {
      return serverImageUrl({ id: existingAsset?.['id'], w: 200 });
    } else if (field.value && existingImageId) {
      return serverImageUrl({ id: existingImageId, w: 200 });
    }
    return null;
  }, [existingAsset, existingImageId, field.value, uploadingFile]);

  return (
    <>
      <FormControl>
        <Stack spacing={1}>
          <FormLabel required={required}>
            {label ?? field.label ?? 'Asset'}
          </FormLabel>
          <Stack alignItems="flex-start" spacing={1}>
            <Box>
              {field.value ? (
                <>
                  {imagePreview ? (
                    <MediaCard
                      onDelete={() => {
                        field.handleValueChange(null);
                      }}
                      onView={() => {
                        if (imagePreview) {
                          setModalMedia(imagePreview.replace(/\?w=\d+/, ''));
                        }
                      }}
                      src={imagePreview as any}
                      alt={''}
                      aspectRatio={aspectRatio}
                    />
                  ) : (
                    <FileCard
                      isLoading={field.value === BUSY}
                      mimeType={mimeType}
                      filename={uploadingFile?.name}
                      imagePreview={imagePreview}
                      onDelete={() => {
                        field.handleValueChange(null);
                      }}
                      downloadHref={
                        field.value === BUSY
                          ? null
                          : serverImageUrl({ id: iriToId(field.value) })
                      }
                    />
                  )}
                </>
              ) : (
                <Button
                  variant="outlined"
                  startIcon={<Upload />}
                  component="label"
                >
                  {getButtonLabel(acceptedTypes)}
                  <input
                    type="file"
                    hidden
                    accept={getAcceptedTypes(acceptedTypes).join(',')}
                    onChange={handleFileChange}
                  />
                </Button>
              )}
            </Box>
            {helperText && <FormHelperText>{helperText}</FormHelperText>}
            <ErrorList errors={field.lastErrorList} />
          </Stack>
        </Stack>
      </FormControl>
      {modalMedia && (
        <FullScreenMediaModal
          src={modalMedia}
          alt=""
          onClose={() => setModalMedia(null)}
        />
      )}
    </>
  );
}
